import {
  Banner,
  Divider,
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { SearchUser } from 'lib-search-types';
import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import * as React from 'react';

import { PhoneNumber } from 'apis/numbers';
import type { TwilioTokenRegion } from 'app/middleware/twilio/voiceSdk/types';
import { isNumberValid, numberFormatterE164 } from 'app/utils/telephonyHelpers';
import { OutboundNumberSelector } from 'components/topbar/telephony/components/OutboundNumberSelector';
import { AddUserResponse } from 'components/usersModal';

import styles from './transferToNumberModal.module.scss';
import UserItem from './userItem';
import '../styles/transfer-to-number-modal.less';

type PropsT = {
  meta: {
    requestAssistedTransfer: AsyncStatus;
    requestBlindTransfer: AsyncStatus;
    searchUser: AsyncStatus;
  };
  ui: {
    isModalShown: boolean;
  };
  users: Array<SearchUser>;
  metaConversation: MetaConversation;
  profile: any;
  presenceMeta: any;
  allPresence: Array<any>;
  presence: any;

  transferToQueueRequests: Array<any>;
  numberTransferRequests: Array<any>;
  transferToAgentRequests: Array<any>;
  fromNumberCountryCode: number | null;
  selectedFromNumber: PhoneNumber | null;
  /**
   * The PBX region of the conversation being transferred. When set, the
   * outbound-number selector and transfer payload are restricted to numbers in
   * this region so the active Twilio conference stays in the same datacenter
   * (IO-4117). Undefined falls back to unrestricted behavior.
   */
  restrictToRegion?: TwilioTokenRegion;

  transferToNumberAssistedRequest: (payload: any) => Promise<any>;
  transferToNumberBlindRequest: (payload: any) => Promise<any>;
  transferToNumberAssistedResolve: (payload: any) => Promise<any>;

  transferToNumberModalOpen: () => void;
  transferToNumberModalClose: () => void;

  getUsersByQuery: (payload: any) => void;

  transferRequestStop: (csid: any) => void;

  userModalAddUser: (payload: any) => Promise<any>;
  transferToNumberResetUsers: () => void;

  notificationAdd: (payload: any) => void;
  notificationRemoveByType: (payload: any) => void;

  telephonyResetHold: () => void;
};

type StateT = {
  init: boolean;
  searchValue: string;
  isValidNumber: boolean;
  highlightedIndex: number | null;
  isContactPointNumbersViewOpen: boolean;
};

export default class TransferToNumberModal extends React.Component<
  PropsT,
  StateT
> {
  input: React.RefObject<any>;

  constructor(props: PropsT) {
    super(props);

    this.input = React.createRef();

    this.state = {
      init: true,
      searchValue: '',
      isValidNumber: false,
      highlightedIndex: null,
      isContactPointNumbersViewOpen: false,
    };
  }

  componentDidUpdate(prevProps: PropsT) {
    if (
      this.props.ui.isModalShown === true &&
      prevProps.ui.isModalShown === false
    ) {
      this.focusOnSearchInput();
      this.logUnresolvedRegionBreadcrumb();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: PropsT) {
    if (
      this.props.ui.isModalShown === true &&
      nextProps.ui.isModalShown === false
    ) {
      this.setState({
        init: true,
        searchValue: '',
        isValidNumber: false,
        highlightedIndex: null,
      });
    } else if (
      this.props.users !== nextProps.users ||
      (this.state.highlightedIndex as number) >= nextProps.users.length
    ) {
      this.setState({ highlightedIndex: null });
    } else if (
      this.props.ui.isModalShown === false &&
      nextProps.ui.isModalShown === true
    ) {
      this.focusOnSearchInput();
    }
  }

  toggleContactPointNumbersView = (isContactPointNumbersViewOpen: boolean) => {
    this.setState({ isContactPointNumbersViewOpen });
  };

  getResolvedTransferFromNumber = (): PhoneNumber | null => {
    const { selectedFromNumber, restrictToRegion } = this.props;

    if (selectedFromNumber === null) {
      return null;
    }

    if (restrictToRegion === undefined) {
      return selectedFromNumber;
    }

    return selectedFromNumber.pbx_region === restrictToRegion
      ? selectedFromNumber
      : null;
  };

  getTransferDisabledReason = (): string | null => {
    const { restrictToRegion, selectedFromNumber } = this.props;

    if (restrictToRegion === undefined) {
      return null;
    }

    if (selectedFromNumber === null) {
      return 'Pick an outbound number in the same region before transferring.';
    }

    if (selectedFromNumber.pbx_region !== restrictToRegion) {
      return 'The current outbound number is in a different region. Pick a matching outbound number before transferring.';
    }

    return null;
  };

  getSourceContactPointValue = (): string => {
    const resolvedFromNumber = this.getResolvedTransferFromNumber();

    return numberFormatterE164({
      phoneNumber: resolvedFromNumber?.e164_format ?? '',
    });
  };

  logUnresolvedRegionBreadcrumb = () => {
    const conversationId = this.props.metaConversation?.conversation?.id;

    if (conversationId == null || this.props.restrictToRegion !== undefined) {
      return;
    }

    Sentry.addBreadcrumb({
      category: 'twilio',
      message: 'transfer_modal_region_unresolved',
      level: 'info',
      data: { conversationId },
    });
  };

  onAssistedTransferClick = (payload?: any) => {
    const disabledReason = this.getTransferDisabledReason();

    if (disabledReason !== null) {
      this.props.notificationAdd({
        title: 'Pick an outbound number first',
        text: disabledReason,
        className: 'info',
        icon: 'i-exclamation',
      });
      return;
    }

    const { requestToUser } = payload ?? {};

    if (
      requestToUser !== undefined &&
      requestToUser !== null &&
      requestToUser.id !== 'newUser'
    ) {
      this._sendAssistedTransferRequest(payload);
    } else {
      const phoneNumber = this.state.searchValue;
      const newEndUser = {
        phone_number: phoneNumber.replace(/ /g, ''),
        user_type: 'contact',
      };

      this.props
        .userModalAddUser(newEndUser)
        .then(unwrapResult)
        .then(({ data: newUser }: AddUserResponse) => {
          const user = {
            id: newUser.id,
            phone_number: newEndUser.phone_number,
          };
          this._sendAssistedTransferRequest({
            requestToUser: user,
          });
        })
        .catch((exception: any) => {
          console.warn(exception);
        });
    }
  };

  onBlindTransferClick = (payload: any) => {
    const disabledReason = this.getTransferDisabledReason();

    if (disabledReason !== null) {
      this.props.notificationAdd({
        title: 'Pick an outbound number first',
        text: disabledReason,
        className: 'info',
        icon: 'i-exclamation',
      });
      return;
    }

    const { requestToUser } = payload ?? {};

    if (requestToUser && requestToUser.id !== 'newUser') {
      this._sendBlindTransferRequest(payload);
    } else {
      const phoneNumber = this.state.searchValue;
      const newEndUser = {
        phone_number: phoneNumber.replace(/ /g, ''),
        user_type: 'contact',
      };

      this.props
        .userModalAddUser(newEndUser)
        .then(unwrapResult)
        .then(({ data: newUser }: AddUserResponse) => {
          const user = {
            id: newUser.id,
            phone_number: newEndUser.phone_number,
          };
          this._sendBlindTransferRequest({
            requestToUser: user,
          });
        })
        .catch((exception: any) => {
          console.warn(exception);
        });
    }
  };

  _sendBlindTransferRequest = (payload: any) => {
    const { requestToUser } = payload;
    const csid = this.props.metaConversation.conversation.id;
    this.props
      .transferToNumberBlindRequest({
        requestToUser: requestToUser,
        requestFromAgent: this.props.profile,
        metaConversation: this.props.metaConversation,
        sourceContactPointValue: this.getSourceContactPointValue(),
      })
      .then(() => {
        this.props.transferToNumberModalClose();
        if (csid) {
          ampli.conversationTransferred({
            conversationId: csid,
            option: 'number',
          });
        }
      })
      .catch((exception: any) => {
        console.warn(exception);
        this.props.transferRequestStop(csid);
        this.props.transferToNumberModalClose();
        const options = {
          title: 'Could not transfer the conversation',
          text: `An issue occurred while trying transfer conversation ${csid}.`,
          className: 'danger',
          icon: 'i-exclamation',
        };
        this.props.notificationAdd(options);
      });
  };

  _sendAssistedTransferRequest = (payload: any) => {
    const { requestToUser } = payload;
    const csid = this.props.metaConversation.conversation.id;
    const metaConversation = this.props.metaConversation;

    this.props
      .transferToNumberAssistedRequest({
        requestToUser: requestToUser,
        requestFromAgent: this.props.profile,
        metaConversation: this.props.metaConversation,
        sourceContactPointValue: this.getSourceContactPointValue(),
      })
      .then((response) => {
        const acceptObj = {
          text: 'Complete transfer',
          action: () => {
            this.props.notificationRemoveByType({
              type: 'transfer_assisted_option_' + csid,
            }); // Remove any assisted transfer option notification.
            try {
              this.props
                .transferToNumberAssistedResolve({
                  metaConversation: metaConversation,
                  accepted: true,
                })
                .then((response) => {
                  if (
                    response.data.accepted === true &&
                    response.data.transfer_type === 'assisted'
                  ) {
                    const csid = response.data.csid;
                    const option = {
                      type: 'transfer_resolved_' + csid,
                      title: `Transfer completed`,
                      text: `You are no longer assigned to #${csid}.`,
                      className: 'success',
                      showDesktop: true,
                      mainAction: {
                        navigate: `/conversation/${csid}`,
                      },
                      icon: 'i-info',
                      time: 15000,
                    };
                    this.props.notificationAdd(option);
                  }
                })
                .catch((exception) => {
                  console.warn(
                    `transferToNumberAssistedResolve complete transfer ${exception}`,
                  );
                });
            } catch (exception) {
              console.warn(exception);
            }
          },
        };
        const rejectObj = {
          text: 'Take back',
          action: () => {
            this.props.notificationRemoveByType({
              type: 'transfer_assisted_option_' + csid,
            }); // Remove any assisted transfer option notification.
            try {
              this.props
                .transferToNumberAssistedResolve({
                  metaConversation: metaConversation,
                  accepted: false,
                })
                .then((response) => {
                  if (
                    response.data.accepted === false &&
                    response.data.transfer_type === 'assisted'
                  ) {
                    const csid = response.data.csid;

                    const option = {
                      type: 'transfer_resolved_' + csid,
                      title: `Transfer take back`,
                      text: `You are again assigned to #${csid}.`,
                      className: 'warning',
                      showDesktop: true,
                      mainAction: {
                        navigate: `/conversation/${csid}`,
                      },
                      icon: 'i-info',
                      time: 15000,
                    };
                    (this.props.telephonyResetHold(), // Remove hold on telephony.
                      this.props.notificationAdd(option));
                  }
                })
                .catch((exception) => {
                  console.warn(
                    `transferToNumberAssistedResolve take back ${exception}`,
                  );
                });
            } catch (exception) {
              console.warn(exception);
            }
          },
        };

        const option = {
          title: 'Transfer in progress',
          text: 'Would you like to complete the transfer of this conversation?',
          type: 'transfer_assisted_option_' + csid,
          icon: 'i-users',
          leftAction: acceptObj,
          rightAction: rejectObj,
          closeAction: rejectObj.action,
          showDesktop: true,
          mainAction: {
            navigate: `/dashboard`,
          },
        };

        this.props.notificationAdd(option);
      })
      .catch((exception) => {
        console.warn(exception);
        this.props.transferRequestStop(csid);
        this.props.transferToNumberModalClose();
        const options = {
          title: 'Could not transfer the conversation',
          text: `An issue occurred while trying transfer conversation ${csid}.`,
          className: 'danger',
          icon: 'i-exclamation',
        };
        this.props.notificationAdd(options);
      });
  };

  hideTransferToNumberModal = () => {
    this.props.transferToNumberModalClose();
  };

  searchInputKeyDown = (e: any) => {
    const { metaConversation, profile } = this.props;
    if (e.which === 13) {
      // enter key pressed.
      let user;
      if (
        this.state.highlightedIndex === null &&
        this.state.isValidNumber === true
      ) {
        user = find(this.props.users, {
          phone_number: this.state.searchValue.replace(/ /g, ''),
        });
      } else {
        user = this.props.users[this.state.highlightedIndex as any];
      }

      if (metaConversation.requester?.id === user.id) {
        const options = {
          title: 'Could not transfer to the contact',
          text: 'This user is already in the conversation',
          className: 'info',
          icon: 'i-exclamation',
        };
        this.props.notificationAdd(options);
        return;
      }

      if (user.id === profile.id) {
        const options = {
          title: 'Could not transfer to the contact',
          text: 'You cannot transfer to yourself.',
          className: 'info',
          icon: 'i-exclamation',
        };
        this.props.notificationAdd(options);
        return;
      }

      if (user) {
        // the user already exists - make a assisted transfer.
        this.onAssistedTransferClick({ requestToUser: user });
      } else if (this.state.isValidNumber === true) {
        console.warn('No user found try to create one and then call');
        this.onAssistedTransferClick();
      }
    } else if (e.which === 40) {
      // Arrow down.
      e.preventDefault();
      try {
        const numberOfOptions = this.props.users.length;

        if (!numberOfOptions) {
          return;
        }
        const highlightedIndex = this.state.highlightedIndex;

        const index =
          highlightedIndex === null || highlightedIndex === numberOfOptions - 1
            ? 0
            : highlightedIndex + 1;

        this.setState({ highlightedIndex: index });
      } catch (exception) {
        console.warn(exception);
      }
    } else if (e.which === 38) {
      // Arrow up.
      e.preventDefault();
      try {
        const numberOfOptions = this.props.users.length;

        if (!numberOfOptions) {
          return;
        }
        const highlightedIndex = this.state.highlightedIndex;

        const index =
          highlightedIndex === 0 || highlightedIndex === null
            ? numberOfOptions - 1
            : highlightedIndex - 1;

        this.setState({ highlightedIndex: index });
      } catch (exception) {
        console.warn(exception);
      }
    }
  };

  onChangeInput = (e: any) => {
    let isValidNumber = false;
    let searchValue = e.target.value;
    this.setState({ init: false });

    const inputValueWithoutWhiteSpaceArray = searchValue.replace(/\s/g, '');
    let inputValueWithoutWhiteSpace: any | null = null;
    if (Array.isArray(inputValueWithoutWhiteSpace)) {
      inputValueWithoutWhiteSpace = (inputValueWithoutWhiteSpace as any).join(
        '',
      );
    } else {
      inputValueWithoutWhiteSpace = inputValueWithoutWhiteSpaceArray;
    }

    try {
      // We validate the number and if valid then format the number.
      if (isNumberValid({ phoneNumber: inputValueWithoutWhiteSpace as any })) {
        isValidNumber = true;
        searchValue = numberFormatterE164({
          phoneNumber: inputValueWithoutWhiteSpace as any,
        });
      } else if (
        isNumberValid({
          phoneNumber: `+${this.props.fromNumberCountryCode}${inputValueWithoutWhiteSpace}`,
        })
      ) {
        isValidNumber = true;
        searchValue = numberFormatterE164({
          phoneNumber: `+${this.props.fromNumberCountryCode}${inputValueWithoutWhiteSpace}`,
        });
      }
    } catch (exception: any) {
      console.error(exception.toString());
    }

    // Updates the search input state.
    this.setState(
      { searchValue: searchValue, isValidNumber: isValidNumber },
      () => {
        this._debouncedRequestGetUsers(searchValue);
      },
    );
  };

  _debouncedRequestGetUsers = debounce((searchQuery: string | null) => {
    this._requestGetUsers(searchQuery);
  }, 500);

  _requestGetUsers = (searchQuery: string | null = null) => {
    if (!searchQuery) {
      console.warn('_requestGetUsers searchQuery is null');
      return;
    }

    if (searchQuery.trim() !== '') {
      this.props.getUsersByQuery({ search: searchQuery });
    } else {
      this.setState({
        init: true,
        searchValue: '',
        isValidNumber: false,
        highlightedIndex: null,
      });
      this.props.transferToNumberResetUsers();
    }
  };

  focusOnSearchInput = () => {
    setTimeout(() => {
      if (this.input.current) {
        this.input.current.focus();
      }
    }, 500);
  };

  render() {
    let disabled = '';
    let csid: number | string | undefined = '';
    let loading: JSX.Element | null = null;
    const transferDisabledReason = this.getTransferDisabledReason();

    if (
      this.props.metaConversation !== null &&
      this.props.metaConversation.conversation.id !== null
    ) {
      csid = this.props.metaConversation.conversation.id;
    }

    if (this.props.meta.requestAssistedTransfer === 'loading') {
      disabled = 'disabled';
    }

    const users = this.props.users.slice();
    if (users.length === 0 && this.state.isValidNumber === true) {
      users.push({
        id: 'newUser',
        phone_number: this.state.searchValue,
        type: 'contact',
        email: null,
        name: null,
        given_name: null,
        family_name: null,
        avatar_url: null,
        company_id: null,
        default_outbound_number: null,
        anonymized_at: null,
        has_scim_enabled: false,
        external_id: null,
        org_id: null,
      });
    }
    const listUser = (user: SearchUser, index: number) => {
      let highlighted = false;
      // We do not want ourselves on the reassign list.
      if (this.state.highlightedIndex === index) {
        highlighted = true;
      }
      return (
        <UserItem
          key={user.id}
          user={user}
          profile={this.props.profile}
          disabled={transferDisabledReason !== null ? 'disabled' : disabled}
          disabledReason={transferDisabledReason ?? undefined}
          metaConversation={this.props.metaConversation}
          highlighted={highlighted}
          asyncStatus={this.props.meta.requestAssistedTransfer}
          onAssistedTransferClick={this.onAssistedTransferClick}
          onBlindTransferClick={this.onBlindTransferClick}
          numberTransferRequests={this.props.numberTransferRequests}
          transferToAgentRequests={this.props.transferToAgentRequests}
        />
      );
    };

    if (users.length === 0 && !this.state.init) {
      loading = (
        <div className={styles.message}>
          No user found or phone number not valid
        </div>
      );
    } else if (users.length === 0 && this.state.init) {
      loading = (
        <Banner
          variant="info"
          icon={null}
          description={
            <Text.Paragraph>
              Enter a number or search for an existing contact by typing in
              their name or phone number (beginning with the country code, eg.
              +1...)
            </Text.Paragraph>
          }
        />
      );
    }

    return (
      <Modal
        className={styles.modal}
        isOpen={this.props.ui.isModalShown}
        onClose={this.hideTransferToNumberModal}
      >
        <ModalHead
          title={`Transfer #${csid} to a different number`}
          onClose={this.hideTransferToNumberModal}
        />

        <ModalBody>
          <FlexBox flexDirection="column" gap="300">
            <div className="search-users-to-transfer">
              <div className={'form-group ' + disabled}>
                <div className="input-group">
                  <input
                    type="search"
                    ref={this.input}
                    id="search-users"
                    disabled={!!disabled}
                    className={'form-control search-users-input ' + disabled}
                    placeholder="Search contacts..."
                    onKeyDown={this.searchInputKeyDown}
                    value={this.state.searchValue}
                    onChange={this.onChangeInput}
                  />
                </div>
              </div>
            </div>

            <div className="section-header">
              <Text.Paragraph variant="1" subdued>
                Select a contact
              </Text.Paragraph>
            </div>

            <div className="row-items">
              {users.map(listUser)}
              {loading}
            </div>

            <Divider />

            {transferDisabledReason !== null ? (
              <Banner
                variant="warning"
                icon={null}
                description={
                  <Text.Paragraph>{transferDisabledReason}</Text.Paragraph>
                }
              />
            ) : null}

            <div className={styles.numberSelector}>
              <OutboundNumberSelector
                isOwnNumbersViewOpen={this.state.isContactPointNumbersViewOpen}
                toggleOwnNumbersView={this.toggleContactPointNumbersView}
                restrictToRegion={this.props.restrictToRegion}
              />
            </div>
          </FlexBox>
        </ModalBody>

        <ModalFoot cancelOnClick={this.hideTransferToNumberModal} />
      </Modal>
    );
  }
}
